import React, { useState, useCallback, useEffect } from 'react';
import ReactLoading from 'react-loading';
import { BeakerIcon, BellIcon, BriefcaseIcon, BroadcastIcon, ContainerIcon, DatabaseIcon, DownloadIcon, GearIcon, GraphIcon,
  HomeIcon, OrganizationIcon, PackageIcon, PersonIcon, 
  SignOutIcon, SmileyIcon, SyncIcon, ToolsIcon, VersionsIcon } from '@primer/octicons-react';
import history from '../../history';
import { graphql, get, post, postAsync, postAsyncMultiple } from '../../post';
import s from './Navigation.css';

/**
 * Points d'entrée de l'import manuel, et libellé de source de chacun.
 *
 * **Une seule table**, d'où la liste des appels *et* les libellés du message final : deux
 * littéraux séparés dérivaient au premier ajout de source, et l'utilisateur lisait alors
 * « une source inconnue » sans qu'aucun test ne le signale.
 */
const IMPORT_SOURCES = {
  '/syncautometerwebdyn': 'webdyn',
  '/syncautosupervisionwebdyn': 'supervision',
  '/syncautometerlora': 'lora',
};

/**
 * Hook personnalisé pour la logique du composant Navigation
 * Gère l'état des menus, les actions asynchrones et la navigation
 * 
 * @param {Object} props - Props du composant
 * @param {Object} props.me - Utilisateur connecté
 * @param {Function} props.emitEvent - Fonction d'émission d'événements
 * @param {Function} props.t - Fonction de traduction
 * @returns {Object} État et fonctions du composant Navigation
 */
export const useNavigationLogic = ({ me, emitEvent, t } = {}) => {
  const [state, setState] = useState({
    loadUpdateAlarm: false,
    loadUpdateAutoMeter: false,
    loadUpdateAutoTable: false,
    loadUpdateLocate: false,
    loadSendAlarmEmail: false,
    menuOpened: undefined
  });

  /**
   * Gère les clics sur la fenêtre pour fermer les menus
   */
  const handleWindowClick = useCallback((e) => {
    const menu = e.target.closest("." + s.navMenuShow);
    if (!menu) {
      setState(prev => ({ ...prev, menuOpened: undefined }));
    }
  }, []);

  /**
   * Gère l'ouverture/fermeture des menus et la navigation
   */
  const handleMenu = useCallback((e, key, link, fct) => {
    e && e.stopPropagation();

    setState(prev => ({
      ...prev,
      menuOpened: prev.menuOpened === key ? undefined : key
    }));

    // Exécuter les actions après la mise à jour de l'état
    if (link) {
      history.push(link);
    }
    if (fct) {
      fct();
    }
  }, []);

  /**
   * Met à jour la géolocalisation
   */
  const updateGeoLocation = useCallback(() => {
    console.log("🔍 [updateGeoLocation] updateGeoLocation", state);
    if (state.loadUpdateLocate) {
      console.log("🔍 [updateGeoLocation] already in progress");
      emitEvent?.("alert", {
        text: t("navigation.tools.updategeolocation.alreadyinprogress")
      });
    } else {
      console.log("🔍 [updateGeoLocation] start update");
      setState(prev => ({ ...prev, loadUpdateLocate: true }));
      post("/updateLocateMissing")
        .then(res => {
          setState(prev => ({ ...prev, loadUpdateLocate: false }));
          emitEvent?.("alert", { text: t("navigation.tools.updategeolocation.done") });
        });
    }
  }, [state.loadUpdateLocate, emitEvent, t]);

  /**
   * Version asynchrone de updateAutoMeter avec callbacks
   */
  const updateAutoMeterAsync = useCallback(async () => {
    if (state.loadUpdateAutoMeter) {
      emitEvent?.("alert", {
        text: t("navigation.tools.syncautometer.alreadyinprogress")
      });
    } else {
      setState(prev => ({ ...prev, loadUpdateAutoMeter: true }));
      
      let totalNb = 0;
      // Vrai dès que le compte rendu final a été rendu, pour que le `catch` ne l'annonce
      // pas une seconde fois : `postAsyncMultiple` rejette quand tout échoue, après avoir
      // appelé `onAllComplete`
      let compteRenduFait = false;
      // Serveurs en échec au sein d'un import par ailleurs abouti
      const accesEnEchec = [];
      
      try {
        await postAsyncMultiple(
          Object.keys(IMPORT_SOURCES),
          null, null, null,
          {
                 onProgress: ({ status, progress, message, requestIndex, totalRequests, requestUrl }) => {
                   // Progression silencieuse
                 },
                 onSuccess: (result, { requestIndex, totalRequests, requestUrl }) => {
                   // Un import peut aboutir tout en ayant échoué sur l'un de ses serveurs :
                   // les chargeurs couvrent tous les accès d'un type, et un accès muet
                   // n'empêche pas les autres. Sans cela, le serveur en échec restait tu.
                   if (result && result.failedAccesses) {
                     accesEnEchec.push(...result.failedAccesses);
                   }

                   if (result && result.data && result.data.addMeterReading) {
                     totalNb += Object.keys(result.data.addMeterReading).length;
                   }
                   // `addSuppervisionReading` — deux « p » — ne correspondait à rien : les
                   // relevés de supervision importés n'étaient jamais comptés
                   if (result && result.data && result.data.addSupervisionReading) {
                     totalNb += Object.keys(result.data.addSupervisionReading).length;
                   }
                 },
                 onError: (error, { requestIndex, totalRequests, requestUrl }) => {
                   // Rien ici : plusieurs chemins d'échec de `postAsync` rejettent sans
                   // passer par ce callback (échec du lancement, délai de polling dépassé,
                   // endpoint refusé). Le compte rendu se fait donc sur `errors`, que
                   // `onAllComplete` reçoit et qui, lui, les couvre tous.
                 },
                 onAllComplete: ({ successful, errors, total, successCount }) => {
              
              setState(prev => ({ ...prev, loadUpdateAutoMeter: false }));
              
              compteRenduFait = true;

              // Chaque échec est journalisé ici, et non dans `onError` : c'est le seul
              // endroit qui les voit tous
              (errors || []).forEach(({ url, error }) => {
                console.error(`>>> import des télérelevés en échec pour ${url} :`, (error && error.message) || error);
              });

              // Deux façons d'échouer sans que tout échoue : un import entier qui n'a
              // pas abouti, ou un serveur muet au sein d'un import abouti. Les deux se
              // disent, là où le succès partiel était annoncé comme un succès complet :
              // le 2026-08-18 en production, l'import LoRa a échoué sur une connexion FTP
              // refusée et l'utilisateur a vu « import terminé ».
              // Le libellé de l'accès, tel que l'administrateur l'a nommé — mais rien ne
              // le rend unique : un accès créé sans libellé reprend celui de son type, si
              // bien que deux serveurs muets pouvaient s'annoncer « Webdyn, Webdyn ». Le
              // code lève l'ambiguïté, et seulement dans ce cas, pour ne pas alourdir le
              // message courant.
              const libelles = accesEnEchec.map(acces => acces.label || acces.code);
              const nomsAcces = accesEnEchec.map((acces, i) => (
                libelles.filter(libelle => libelle === libelles[i]).length > 1
                  ? `${libelles[i]} (${acces.code})`
                  : libelles[i]
              ));

              const enEchec = [
                ...(errors || []).map(({ url }) => t(`navigation.tools.syncautometer.sources.${IMPORT_SOURCES[url] || 'unknown'}`)),
                ...nomsAcces,
              ];

              accesEnEchec.forEach(acces => {
                console.error(`>>> import des télérelevés : accès ${acces.code} en échec :`, acces.motif);
              });

              if (successCount > 0 && enEchec.length > 0) {
                const sources = enEchec.join(", ");

                emitEvent?.("alert", {
                  text: t("navigation.tools.syncautometer.partial", { count: totalNb, sources })
                });
              } else if (successCount > 0) {
                emitEvent?.("alert", {
                  text: t("navigation.tools.syncautometer.done", { count: totalNb })
                });
              } else {
                emitEvent?.("alert", {
                  text: t("navigation.tools.syncautometer.error")
                });
              }
            }
          }, {
            // Un import couvre desormais tous les acces d'un type, en sequence : les deux
            // acces Webdyn de la production demandent a eux seuls environ 25 minutes.
            maxAttempts: 720, // 720 tentatives × 5 secondes = 60 minutes
            interval: 5000,   // 5 secondes entre chaque vérification
          }
        );
      } catch (error) {
        console.error("❌ Erreur lors de l'import des télérelevés:", error);
        setState(prev => ({ ...prev, loadUpdateAutoMeter: false }));

        // Tout échouer fait rejeter `postAsyncMultiple` *après* `onAllComplete` : sans ce
        // garde, l'utilisateur recevait deux fois le même message
        if (!compteRenduFait) {
          emitEvent?.("alert", {
            text: t("navigation.tools.syncautometer.error")
          });
        }
      }
    }
  }, [state.loadUpdateAutoMeter, emitEvent, t]);

  /**
   * Met à jour les alarmes de manière asynchrone
   */
  const updateAlarmAsync = useCallback(async () => {
    if (state.loadUpdateAlarm) {
      emitEvent?.("alert", {
        text: t("navigation.tools.updatealarm.alreadyinprogress")
      });
    } else {
      setState(prev => ({ ...prev, loadUpdateAlarm: true }));
      
      try {
        await postAsync("/updateAlarm", null, null, null, {
                 onSuccess: (result) => {
                   setState(prev => ({ ...prev, loadUpdateAlarm: false }));
                   emitEvent?.("alert", { text: t("navigation.tools.updatealarm.done") });
                 },
                 onError: (error) => {
                   setState(prev => ({ ...prev, loadUpdateAlarm: false }));
                   emitEvent?.("alert", { text: t("navigation.tools.updatealarm.error") });
                 }
        }, {
          maxAttempts: 300, // 300 tentatives × 5 secondes = 25 minutes
          interval: 5000,    // 5 secondes entre chaque vérification
          timeout: 300000      // 300000 ms = 5 minutes
        });
      } catch (error) {
        console.error("❌ Erreur lors de la mise à jour des alarmes:", error);
        setState(prev => ({ ...prev, loadUpdateAlarm: false }));
        emitEvent?.("alert", {
          text: t("navigation.tools.updatealarm.error")
        });
      }
    }
  }, [state.loadUpdateAlarm, emitEvent, t]);

  /**
   * Met à jour les tables automatiques
   */
  const updateAutoTableAsync = useCallback(async () => {
    if (state.loadUpdateAutoTable) {
      emitEvent?.("alert", {
        text: t("navigation.tools.updateautotable.alreadyinprogress")
      });
    } else {
      setState(prev => ({ ...prev, loadUpdateAutoTable: true }));
      
      try {
        await postAsync("/autoTableUpdate", null, null, null, {
                 onSuccess: (result) => {
                   setState(prev => ({ ...prev, loadUpdateAutoTable: false }));
                   emitEvent?.("alert", { text: t("navigation.tools.updateautotable.done") });
                 },
                 onError: (error) => {
                   setState(prev => ({ ...prev, loadUpdateAutoTable: false }));
                   emitEvent?.("alert", { text: t("navigation.tools.updateautotable.error") });
                 }
        }, {
          maxAttempts: 300, // 300 tentatives × 5 secondes = 25 minutes
          interval: 5000,    // 5 secondes entre chaque vérification
          timeout: 300000      // 300000 ms = 5 minutes
        });
      } catch (error) {
        console.error("❌ Erreur lors de la mise à jour des tables automatiques:", error);
        setState(prev => ({ ...prev, loadUpdateAutoTable: false }));
        emitEvent?.("alert", {
          text: t("navigation.tools.updateautotable.error")
        });
      }
    }
  }, [state.loadUpdateAutoTable, emitEvent, t]);

  /**
   * Teste l'envoi d'email d'alarme
   */
  const testSendAlarmEmail = useCallback(async () => {
    if (state.loadSendAlarmEmail) {
      emitEvent?.("alert", {
        text: t("navigation.tools.testsendalarmemail.alreadyinprogress")
      });
    } else {
      setState(prev => ({ ...prev, loadSendAlarmEmail: true }));
      
      try {
        await postAsync("/testSendAlarmEmail", null, null, null, {
                 onSuccess: (result) => {
                   setState(prev => ({ ...prev, loadSendAlarmEmail: false }));
                   emitEvent?.("alert", { text: t("navigation.tools.testsendalarmemail.done") });
                 },
                 onError: (error) => {
                   setState(prev => ({ ...prev, loadSendAlarmEmail: false }));
                   emitEvent?.("alert", { text: t("navigation.tools.testsendalarmemail.error") });
                 }
        }, {
          maxAttempts: 300, // 300 tentatives × 5 secondes = 25 minutes
          interval: 5000,    // 5 secondes entre chaque vérification
          timeout: 300000      // 300000 ms = 5 minutes
        });
      } catch (error) {
        console.error("❌ Erreur lors de l'envoi de l'email d'alarme:", error);
        setState(prev => ({ ...prev, loadSendAlarmEmail: false }));
        emitEvent?.("alert", {
          text: t("navigation.tools.testsendalarmemail.error")
        });
      }
    }
  }, [state.loadSendAlarmEmail, emitEvent, t]);

  /**
   * Retourne les données de l'icône appropriée selon le type
   */
  const getIconData = useCallback((IconComponent) => {
    return {
      component: IconComponent,
      size: 24
    };
  }, []);

  /**
   * Fonction de compatibilité pour rendre les icônes (à utiliser dans les composants)
   */
  const getIconTag = useCallback((IconComponent) => {
    return <IconComponent size={24} />;
  }, []);

  return {
    state,
    handleMenu,
    handleWindowClick,
    getIconData,
    getIconTag,
    updateAlarmAsync,
    updateAutoMeterAsync,
    updateAutoTableAsync,
    updateGeoLocation,
    testSendAlarmEmail
  };
};

/**
 * Détermine les options de compte selon le type d'utilisateur
 * 
 * @param {Object} me - Utilisateur connecté
 * @param {Function} t - Fonction de traduction
 * @param {Function} getIconTag - Fonction pour obtenir les icônes
 * @returns {Array} Options de compte
 */
export const getAccountOptions = (me, t, getIconTag) => {
  const accountOptions = [
    { icon: getIconTag(SmileyIcon), label: t("navigation.myprofil"), link: "/profile" }
  ];
  
  if (me && me.type < 40) {
    accountOptions.push({
      icon: getIconTag(BellIcon),
      label: t("navigation.mynotification"),
      link: "/setnotification"
    });
  }

  if (me && me.type >= 40) {
    accountOptions.push({
      icon: getIconTag(BellIcon),
      label: t("navigation.mynotification"),
      link: "/setnotificationuser"
    });
  }

  if (me && me.type < 20) {
    // Templates d'email pour admins et superadmins
    accountOptions.push({
      icon: getIconTag(BeakerIcon),
      label: t("navigation.tools.emailtemplates"),
      link: "/tools/email-templates"
    });

    // Paramétrage applicatif réservé au superadmin
    if (me.type === 0) {
      accountOptions.push({
        icon: getIconTag(GearIcon),
        label: t("navigation.adminsettings"),
        link: "/admin/settings"
      });
      accountOptions.push({
        icon: getIconTag(GearIcon),
        label: t("navigation.adminremoteaccess"),
        link: "/admin/remoteaccess"
      });
    }
  }
  
  accountOptions.push({
    icon: getIconTag(SignOutIcon),
    label: t("navigation.disconnect"),
    link: "/logout"
  });
  
  return accountOptions;
};

/**
 * Détermine si un menu doit être affiché selon le type d'utilisateur
 * 
 * @param {string} menuKey - Clé du menu
 * @param {Object} me - Utilisateur connecté
 * @returns {boolean} True si le menu doit être affiché
 */
export const shouldShowMenu = (menuKey, me) => {
  if (!me) return false;
  
  switch (menuKey) {
    case 'admin':
      return me.type === 0;
    case 'buildManager':
      return me.type < 21;
    case 'technicalManager':
      return me.type < 20 || me.type === 31;
    case 'user':
      return me.type < 20;
    case 'housing':
      return true; // Le menu housing est affiché pour tous les utilisateurs connectés
    case 'tools':
      return me.type < 20;
    case 'account':
      return true;
    default:
      return false;
  }
};

/**
 * Détermine si une action est en cours
 * 
 * @param {Array} loadUpdate - Tableau des actions en cours
 * @returns {boolean} True si une action est en cours
 */
export const ActionInProgress = (loadUpdate = []) => {
  return loadUpdate.some(load => load);
};

/**
 * Accès FTP en cache, par utilisateur.
 *
 * La navigation est reconstruite à chaque changement de page ; sans ce cache, chaque page
 * relancerait la même requête pour une liste qui ne change qu'à l'administration. Le cache
 * porte l'`uid` de l'utilisateur pour lequel il a été rempli : une reconnexion sous un
 * autre compte le remplace au lieu d'en hériter. `null` signifie « pas encore chargée ».
 */
let ftpAccessesCache = null;

/**
 * Vide le cache des accès FTP.
 *
 * ⚠️ **À appeler après toute création, suppression ou renommage d'accès** : sans cela le
 * menu servirait la liste d'avant pour le reste de la session, et la page d'un accès
 * fraîchement créé resterait inatteignable — le symptôme même de l'issue #336.
 */
export const resetFtpAccessesCache = () => {
  ftpAccessesCache = null;
};

/**
 * Accès FTP à proposer dans le menu « Outils ».
 *
 * Le menu portait trois entrées écrites en dur (`/ftpfiles/webdyn`, `webdynSupervision`,
 * `lora`) : un second serveur du même type n'avait donc aucune page atteignable, alors que
 * ses fichiers étaient bien importés (issue #336). La liste vient désormais des accès
 * enregistrés.
 *
 * Un échec de chargement laisse la liste vide plutôt que d'interrompre la navigation : le
 * menu perd ses entrées FTP, le reste de l'application fonctionne.
 *
 * @param {Object} props
 * @param {Object} props.context - contexte applicatif, pour `context.fetch`
 * @param {Object} props.me - utilisateur connecté
 * @returns {Array<{code: string, label: string, type: string}>}
 */
export const useFtpAccesses = ({ context, me } = {}) => {
  const pourCetUtilisateur = ftpAccessesCache && me && ftpAccessesCache.uid === me.uid;
  const [accesses, setAccesses] = useState(pourCetUtilisateur ? ftpAccessesCache.accesses : []);

  useEffect(() => {
    // Les pages de fichiers FTP sont réservées au superadmin et aux administrateurs
    if (!me || me.type >= 10) return undefined;
    if (!context || !context.fetch) return undefined;

    // Liste déjà en cache pour cet utilisateur : rien à redemander
    if (ftpAccessesCache && ftpAccessesCache.uid === me.uid) {
      setAccesses(ftpAccessesCache.accesses);
      return undefined;
    }

    let abandonne = false;

    graphql(context.fetch, '{ftpAccesses{code, label, type}}')
      .then(res => {
        if (abandonne || !res || !res.ftpAccesses) return;
        ftpAccessesCache = { uid: me.uid, accesses: res.ftpAccesses };
        setAccesses(res.ftpAccesses);
      })
      .catch(error => {
        console.error("Impossible de charger les accès FTP du menu :", error);
      });

    return () => { abandonne = true; };
  }, [context, me]);

  return accesses;
};
